import React, { memo, useEffect, useMemo, useRef, useState } from "react";
import { isDarkMode } from "../../../utils/constants";
import { isMobileBrowser } from "../../../utils/user_agent";
import {
  createMainSeriesData,
  formatCrosshairTime,
  formatTickMark,
  formatPriceValue,
  getInitialLogicalRange,
  logicalRangesClose,
  normalizeChartScale,
  resolveChartScreenshotCanvas,
  zoomLogicalRange,
} from "./lightweightChartUtils";
import {
  installAttributionHider,
  loadLightweightCharts,
} from "./lightweightChartsLoader";
import { parseRawDate } from "./utils";

const toTradeNumber = (value) => {
  if (value == null || value === "") return null;
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }
  const cleaned = String(value).replace(/,/g, "").trim();
  if (!cleaned) return null;
  const num = Number(cleaned);
  return Number.isFinite(num) ? num : null;
};

const inferTradeSide = (entry, stop, target) => {
  if (entry == null) return null;
  if (stop != null && stop !== entry) {
    return stop < entry ? "long" : "short";
  }
  if (target != null && target !== entry) {
    return target > entry ? "long" : "short";
  }
  return null;
};

const pickTradeAnchorTimestampMs = (tradeProposal, messageThread) => {
  const candidates = [
    tradeProposal?.entry_bar_time,
    tradeProposal?.entryBarTime,
    tradeProposal?.entry_bar_datetime,
    tradeProposal?.entryBarDatetime,
    tradeProposal?.entry_bar_at,
    tradeProposal?.entryBarAt,
    tradeProposal?.current_price_updated_at,
    tradeProposal?.currentPriceUpdatedAt,
    tradeProposal?.created_at,
    tradeProposal?.createdAt,
    messageThread?.createdAt,
    messageThread?.created_at,
  ];

  for (const candidate of candidates) {
    if (candidate == null || candidate === "") continue;
    if (candidate instanceof Date) {
      const ms = candidate.getTime();
      if (Number.isFinite(ms)) return ms;
      continue;
    }
    if (typeof candidate === "number") {
      if (Number.isFinite(candidate)) {
        return candidate < 1e12 ? candidate * 1000 : candidate;
      }
      continue;
    }
    const parsed = Date.parse(String(candidate));
    if (Number.isFinite(parsed)) return parsed;
  }

  return null;
};

const buildTradeAnchorIndex = ({
  tradeProposal,
  messageThread,
  tickerPrices,
  scale,
}) => {
  if (!Array.isArray(tickerPrices) || tickerPrices.length === 0) return null;

  const targetMs = pickTradeAnchorTimestampMs(tradeProposal, messageThread);
  if (!Number.isFinite(targetMs)) {
    return tickerPrices.length - 1;
  }

  let bestIndex = null;
  let bestDiff = Number.POSITIVE_INFINITY;

  tickerPrices.forEach((entry, idx) => {
    const rawTime =
      scale === "1d" || scale === "1w" || scale === "1mo"
        ? entry?.date ?? entry?.datetime
        : entry?.datetime ?? entry?.date;
    const parsed = parseRawDate(rawTime, scale);
    const candidateMs = parsed?.getTime?.();
    if (!Number.isFinite(candidateMs)) return;

    const diff = Math.abs(candidateMs - targetMs);
    if (diff < bestDiff) {
      bestDiff = diff;
      bestIndex = idx;
    }
  });

  return bestIndex != null ? bestIndex : tickerPrices.length - 1;
};

const getPalette = () => {
  if (isDarkMode) {
    return {
      background: "#0d1117",
      text: "#cbd5e1",
      mutedText: "#94a3b8",
      border: "rgba(148, 163, 184, 0.24)",
      grid: "rgba(148, 163, 184, 0.12)",
      crosshair: "rgba(226, 232, 240, 0.26)",
      line: "#7aa2ff",
      up: "#21c58e",
      down: "#ef5f6b",
      entry: "#60a5fa",
      target: "#4ade80",
      stop: "#f87171",
      extendedHours: "#F5C84B",
      extendedHoursText: "#111827",
      premarketFill: "rgba(245, 200, 75, 0.09)",
      afterhoursFill: "rgba(245, 158, 11, 0.1)",
      sessionDivider: "rgba(148, 163, 184, 0.28)",
    };
  }

  return {
    background: "#f8fafc",
    text: "#475569",
    mutedText: "#64748b",
    border: "rgba(100, 116, 139, 0.18)",
    grid: "rgba(100, 116, 139, 0.12)",
    crosshair: "rgba(30, 41, 59, 0.18)",
    line: "#2563eb",
    up: "#16a34a",
    down: "#dc2626",
    entry: "#2563eb",
    target: "#16a34a",
    stop: "#dc2626",
    extendedHours: "#D99A00",
    extendedHoursText: "#111827",
    premarketFill: "rgba(217, 154, 0, 0.08)",
    afterhoursFill: "rgba(217, 119, 6, 0.1)",
    sessionDivider: "rgba(71, 85, 105, 0.22)",
  };
};

const SESSION_OVERLAY_EMPTY = { bands: [], dividers: [] };
const SESSION_PREMARKET = "premarket";
const SESSION_REGULAR = "regular";
const SESSION_AFTERHOURS = "afterhours";
const US_EQUITY_MARKET_TIME_ZONE = "America/New_York";
const INTRADAY_SESSION_SCALES = new Set([
  "1m",
  "2m",
  "3m",
  "5m",
  "10m",
  "15m",
  "30m",
  "1h",
  "2h",
  "3h",
  "4h",
]);
const US_EQUITY_PREMARKET_START_MINUTES = 4 * 60;
const US_EQUITY_REGULAR_START_MINUTES = 9 * 60 + 30;
const US_EQUITY_REGULAR_END_MINUTES = 16 * 60;
const US_EQUITY_AFTERHOURS_END_MINUTES = 20 * 60;
const zonedDateTimeFormatterCache = new Map();

const normalizeAssetType = (assetType) =>
  String(assetType || "")
    .trim()
    .toLowerCase();

const shouldShowUsEquitySessions = ({ assetType, scale }) =>
  normalizeAssetType(assetType) === "stocks" &&
  INTRADAY_SESSION_SCALES.has(scale);

const getZonedDateTimeFormatter = (timeZone) => {
  if (!zonedDateTimeFormatterCache.has(timeZone)) {
    zonedDateTimeFormatterCache.set(
      timeZone,
      new Intl.DateTimeFormat("en-US", {
        timeZone,
        weekday: "short",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hourCycle: "h23",
      })
    );
  }

  return zonedDateTimeFormatterCache.get(timeZone);
};

const getZonedDateTimeParts = (absoluteTime, timeZone) => {
  if (!Number.isFinite(absoluteTime)) return null;

  const date = new Date(absoluteTime * 1000);
  if (Number.isNaN(date.getTime())) return null;

  const parts = getZonedDateTimeFormatter(timeZone).formatToParts(date);
  const get = (type) => parts.find((part) => part.type === type)?.value || null;
  const weekday = get("weekday");
  const year = Number(get("year"));
  const month = Number(get("month"));
  const day = Number(get("day"));
  const hour = Number(get("hour"));
  const minute = Number(get("minute"));

  if (
    !weekday ||
    !Number.isFinite(year) ||
    !Number.isFinite(month) ||
    !Number.isFinite(day) ||
    !Number.isFinite(hour) ||
    !Number.isFinite(minute)
  ) {
    return null;
  }

  return {
    weekday,
    year,
    month,
    day,
    hour,
    minute,
  };
};

const getUsEquitySessionType = (absoluteTime) => {
  const parts = getZonedDateTimeParts(
    absoluteTime,
    US_EQUITY_MARKET_TIME_ZONE
  );
  if (!parts) return null;

  if (parts.weekday === "Sat" || parts.weekday === "Sun") return null;

  const minutes = parts.hour * 60 + parts.minute;
  if (
    minutes < US_EQUITY_PREMARKET_START_MINUTES ||
    minutes >= US_EQUITY_AFTERHOURS_END_MINUTES
  ) {
    return null;
  }
  if (minutes < US_EQUITY_REGULAR_START_MINUTES) {
    return SESSION_PREMARKET;
  }
  if (minutes < US_EQUITY_REGULAR_END_MINUTES) {
    return SESSION_REGULAR;
  }
  return SESSION_AFTERHOURS;
};

const getSessionDayKey = (absoluteTime) => {
  const parts = getZonedDateTimeParts(
    absoluteTime,
    US_EQUITY_MARKET_TIME_ZONE
  );
  if (!parts) return null;

  return `${parts.year}-${String(parts.month).padStart(2, "0")}-${String(
    parts.day
  ).padStart(2, "0")}`;
};

const buildSessionOverlayConfig = ({ candleData, assetType, scale }) => {
  if (
    !shouldShowUsEquitySessions({ assetType, scale }) ||
    !Array.isArray(candleData) ||
    candleData.length === 0
  ) {
    return SESSION_OVERLAY_EMPTY;
  }

  const byDay = new Map();

  candleData.forEach((entry) => {
    const time = Number(entry?.time);
    const absoluteTime = Number(entry?.absoluteTime);
    if (!Number.isFinite(time) || !Number.isFinite(absoluteTime)) return;

    const sessionType = getUsEquitySessionType(absoluteTime);
    if (!sessionType) return;

    const dayKey = getSessionDayKey(absoluteTime);
    if (!dayKey) return;

    let daySessions = byDay.get(dayKey);
    if (!daySessions) {
      daySessions = {
        [SESSION_PREMARKET]: null,
        [SESSION_REGULAR]: null,
        [SESSION_AFTERHOURS]: null,
      };
      byDay.set(dayKey, daySessions);
    }

    if (!daySessions[sessionType]) {
      daySessions[sessionType] = { startTime: time, endTime: time };
      return;
    }

    daySessions[sessionType].startTime = Math.min(
      daySessions[sessionType].startTime,
      time
    );
    daySessions[sessionType].endTime = Math.max(
      daySessions[sessionType].endTime,
      time
    );
  });

  const bands = [];
  const dividers = [];

  byDay.forEach((daySessions) => {
    const premarket = daySessions[SESSION_PREMARKET];
    const regular = daySessions[SESSION_REGULAR];
    const afterhours = daySessions[SESSION_AFTERHOURS];

    if (premarket) {
      bands.push({ ...premarket, type: SESSION_PREMARKET });
    }
    if (afterhours) {
      bands.push({ ...afterhours, type: SESSION_AFTERHOURS });
    }
    if (regular && premarket) {
      dividers.push({ time: regular.startTime, kind: "market-open" });
    }
    if (afterhours) {
      dividers.push({ time: afterhours.startTime, kind: "market-close" });
    }
  });

  if (bands.length === 0 && dividers.length === 0) {
    return SESSION_OVERLAY_EMPTY;
  }

  return {
    bands: bands.sort((a, b) => a.startTime - b.startTime),
    dividers: dividers.sort((a, b) => a.time - b.time),
  };
};

const buildChartOptions = ({ library, scale, assetType, isMobile }) => {
  const palette = getPalette();
  const isHighTimeframe =
    scale === "1d" || scale === "1w" || scale === "1mo";

  return {
    layout: {
      background: { color: palette.background },
      textColor: palette.text,
      fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    },
    attributionLogo: { visible: false },
    grid: {
      vertLines: { color: palette.grid },
      horzLines: { color: palette.grid },
    },
    crosshair: {
      mode: library?.CrosshairMode?.Normal ?? 0,
      vertLine: {
        color: palette.crosshair,
        width: 1,
        style: 2,
      },
      horzLine: {
        color: palette.crosshair,
        width: 1,
        style: 2,
      },
    },
    rightPriceScale: {
      borderColor: palette.border,
      scaleMargins: { top: 0.08, bottom: 0.08 },
    },
    leftPriceScale: {
      visible: false,
    },
    timeScale: {
      borderColor: palette.border,
      timeVisible: !isHighTimeframe,
      secondsVisible: false,
      rightOffset: 5,
      rightBarStaysOnScroll: true,
      barSpacing: isMobile ? 8 : 10,
      minBarSpacing: 3,
      tickMarkFormatter: (time) => formatTickMark(time, scale),
    },
    handleScroll: isMobile
      ? {
          mouseWheel: false,
          pressedMouseMove: false,
          horzTouchDrag: true,
          vertTouchDrag: false,
        }
      : {
          mouseWheel: true,
          pressedMouseMove: true,
          horzTouchDrag: true,
          vertTouchDrag: false,
        },
    handleScale: isMobile
      ? {
          mouseWheel: false,
          axisPressedMouseMove: false,
          pinch: true,
        }
      : {
          mouseWheel: true,
          axisPressedMouseMove: true,
          pinch: true,
        },
    localization: {
      timeFormatter: (time) => formatCrosshairTime(time, scale),
      priceFormatter: (value) => formatPriceValue(value, assetType),
    },
  };
};

const scheduleRangeApply = ({
  chart,
  range,
  suppressRangeSyncRef,
}) => {
  if (!chart || !range) return;
  const timeScale = chart.timeScale();
  if (!timeScale?.setVisibleLogicalRange) return;

  suppressRangeSyncRef.current = true;
  timeScale.setVisibleLogicalRange(range);
  window.requestAnimationFrame(() => {
    suppressRangeSyncRef.current = false;
  });
};

const resetOhlcBox = (element) => {
  if (!element) return;
  element.innerHTML =
    '<span>O --</span><span>H --</span><span>L --</span><span>C --</span><span>--</span>';
};

const updateOhlcBox = (element, candle, assetType) => {
  if (!element) return;
  if (!candle || !Number.isFinite(candle.open)) {
    resetOhlcBox(element);
    return;
  }

  const change = Number(candle.close) - Number(candle.open);
  const changePct =
    Number(candle.open) !== 0 ? change / Number(candle.open) : null;
  const changeColor =
    change >= 0 ? getPalette().up : getPalette().down;
  const changeText =
    changePct == null
      ? "--"
      : `${changePct >= 0 ? "+" : ""}${(changePct * 100).toFixed(2)}%`;

  element.innerHTML =
    `<span>O ${formatPriceValue(candle.open, assetType)}</span>` +
    `<span>H ${formatPriceValue(candle.high, assetType)}</span>` +
    `<span>L ${formatPriceValue(candle.low, assetType)}</span>` +
    `<span>C ${formatPriceValue(candle.close, assetType)}</span>` +
    `<span style="color:${changeColor}">${changeText}</span>`;
};

const INTRADAY_SCALE_SECONDS = {
  "1m": 60,
  "2m": 2 * 60,
  "3m": 3 * 60,
  "5m": 5 * 60,
  "10m": 10 * 60,
  "15m": 15 * 60,
  "30m": 30 * 60,
  "1h": 60 * 60,
  "2h": 2 * 60 * 60,
  "3h": 3 * 60 * 60,
  "4h": 4 * 60 * 60,
};

const nowInChartWallClockMs = () => {
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Los_Angeles",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  });
  const parts = formatter.formatToParts(new Date());
  const get = (type) => parts.find((part) => part.type === type)?.value || "0";
  return Date.UTC(
    Number(get("year")),
    Number(get("month")) - 1,
    Number(get("day")),
    Number(get("hour")),
    Number(get("minute")),
    Number(get("second")),
    0
  );
};

const formatCountdownText = (remainingSeconds) => {
  const total = Math.max(0, Math.floor(remainingSeconds));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;

  if (hours > 0) {
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  }

  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};

const hideLivePriceLabel = (element) => {
  if (!element) return;
  element.style.display = "none";
};

const resolveCountdownState = ({ scale, latestCandle }) => {
  if (!latestCandle || !Number.isFinite(latestCandle.time)) return null;

  const bucketSeconds = INTRADAY_SCALE_SECONDS[scale];
  if (!Number.isFinite(bucketSeconds) || bucketSeconds <= 0) return null;

  const nowCandidates = [
    Math.floor(Date.now() / 1000),
    Math.floor(nowInChartWallClockMs() / 1000),
  ];

  const candidates = nowCandidates
    .map((nowSeconds) => {
      const bucketStart = Math.floor(nowSeconds / bucketSeconds) * bucketSeconds;
      return {
        nowSeconds,
        bucketStart,
        diff: Math.abs(latestCandle.time - bucketStart),
      };
    })
    .sort((a, b) => a.diff - b.diff);

  const best = candidates[0];
  if (!best || best.diff > bucketSeconds * 2) return null;

  return {
    text: formatCountdownText(best.bucketStart + bucketSeconds - best.nowSeconds),
  };
};

const updateLivePriceLabel = ({
  element,
  chart,
  series,
  latestCandle,
  scale,
  assetType,
  isMobile,
  extendedHoursQuote = null,
}) => {
  if (!element || !chart || !series || !latestCandle) {
    hideLivePriceLabel(element);
    return;
  }

  const extendedPrice = toTradeNumber(extendedHoursQuote?.price);
  const referencePrice =
    toTradeNumber(extendedHoursQuote?.referencePrice) ??
    toTradeNumber(latestCandle.open);
  const displayPrice = extendedPrice ?? latestCandle.close;
  const countdown = extendedPrice == null
    ? resolveCountdownState({ scale, latestCandle })
    : null;
  const yCoordinate = series.priceToCoordinate?.(displayPrice);
  const containerHeight = chart?.options?.()?.height || element.parentElement?.clientHeight || 0;
  if (!Number.isFinite(yCoordinate) || !Number.isFinite(containerHeight) || containerHeight <= 0) {
    hideLivePriceLabel(element);
    return;
  }

  const palette = getPalette();
  const isUp = Number(displayPrice) >= Number(referencePrice);
  const isExtendedHours = extendedPrice != null;
  const backgroundColor = isExtendedHours
    ? palette.extendedHours
    : isUp
      ? palette.up
      : palette.down;
  const priceText = formatPriceValue(displayPrice, assetType);
  const detailText = extendedPrice != null
    ? extendedHoursQuote?.label || "Extended hours"
    : countdown?.text || "";

  element.innerHTML =
    `<span style="font-size:${isMobile ? "10px" : "11px"};font-weight:600;line-height:1;">${priceText}</span>` +
    (detailText
      ? `<span style="font-size:${isMobile ? "9px" : "10px"};font-weight:500;line-height:1.05;opacity:0.9;">${detailText}</span>`
      : "");

  element.style.display = "flex";
  element.style.background = backgroundColor;
  element.style.color = isExtendedHours ? palette.extendedHoursText : "#ffffff";
  element.style.flexDirection = "column";
  element.style.alignItems = "flex-start";
  element.style.gap = detailText ? "2px" : "0px";

  const labelHeight = Math.max(element.offsetHeight || (detailText ? 32 : 22), 22);
  const halfHeight = labelHeight / 2;
  const clampedCenter = Math.max(halfHeight + 2, Math.min(yCoordinate, containerHeight - halfHeight - 2));
  element.style.top = `${clampedCenter}px`;
};

const resizeOverlayCanvas = (canvas) => {
  if (!canvas) return null;
  const width = Math.max(canvas.clientWidth || 0, 0);
  const height = Math.max(canvas.clientHeight || 0, 0);
  const dpr =
    typeof window !== "undefined" && Number.isFinite(window.devicePixelRatio)
      ? window.devicePixelRatio
      : 1;
  const nextWidth = Math.max(Math.round(width * dpr), 1);
  const nextHeight = Math.max(Math.round(height * dpr), 1);

  if (canvas.width !== nextWidth) {
    canvas.width = nextWidth;
  }
  if (canvas.height !== nextHeight) {
    canvas.height = nextHeight;
  }

  const context = canvas.getContext("2d");
  if (!context) return null;
  context.setTransform(dpr, 0, 0, dpr, 0, 0);
  return {
    context,
    width,
    height,
  };
};

const clearOverlayCanvas = (canvas) => {
  const resized = resizeOverlayCanvas(canvas);
  if (!resized) return;
  resized.context.clearRect(0, 0, resized.width, resized.height);
};

const estimateBarSpacing = (chart, fallbackWidth) => {
  const visibleRange = chart?.timeScale?.()?.getVisibleLogicalRange?.();
  if (
    !visibleRange ||
    !Number.isFinite(visibleRange.from) ||
    !Number.isFinite(visibleRange.to) ||
    visibleRange.to <= visibleRange.from
  ) {
    return Math.max(fallbackWidth / 80, 6);
  }

  return Math.max(
    fallbackWidth / Math.max(visibleRange.to - visibleRange.from, 1),
    6
  );
};

const drawSessionOverlay = ({ chart, overlay, context, width, height }) => {
  if (
    !chart ||
    !context ||
    !Number.isFinite(width) ||
    !Number.isFinite(height) ||
    (!Array.isArray(overlay?.bands) && !Array.isArray(overlay?.dividers))
  ) {
    return;
  }

  const palette = getPalette();
  const timeScale = chart.timeScale?.();
  if (!timeScale) return;

  const barSpacing = estimateBarSpacing(chart, width);
  const halfBar = Math.max(barSpacing / 2, 3);

  (overlay?.bands || []).forEach((band) => {
    if (
      !band ||
      !Number.isFinite(band.startTime) ||
      !Number.isFinite(band.endTime)
    ) {
      return;
    }

    const startCoordinate = timeScale.timeToCoordinate?.(band.startTime);
    const endCoordinate = timeScale.timeToCoordinate?.(band.endTime);
    if (!Number.isFinite(startCoordinate) || !Number.isFinite(endCoordinate)) {
      return;
    }

    const left = Math.max(0, Math.min(startCoordinate, endCoordinate) - halfBar);
    const right = Math.min(width, Math.max(startCoordinate, endCoordinate) + halfBar);
    if (!Number.isFinite(left) || !Number.isFinite(right) || right <= left) {
      return;
    }

    context.save();
    context.fillStyle =
      band.type === SESSION_AFTERHOURS
        ? palette.afterhoursFill
        : palette.premarketFill;
    context.fillRect(left, 0, right - left, height);
    context.restore();
  });

  (overlay?.dividers || []).forEach((divider) => {
    if (!divider || !Number.isFinite(divider.time)) return;

    const x = timeScale.timeToCoordinate?.(divider.time);
    if (!Number.isFinite(x) || x < -2 || x > width + 2) return;

    context.save();
    context.strokeStyle = palette.sessionDivider;
    context.lineWidth = 1;
    context.setLineDash([4, 4]);
    context.beginPath();
    context.moveTo(Math.round(x) + 0.5, 0);
    context.lineTo(Math.round(x) + 0.5, height);
    context.stroke();
    context.restore();
  });
};

const drawTradeOverlay = ({ chart, series, overlay, context, width, height }) => {
  if (!chart || !series || !context || !Number.isFinite(width) || !Number.isFinite(height)) {
    return;
  }

  if (!Array.isArray(overlay?.segments) || overlay.segments.length === 0) {
    return;
  }

  const timeScale = chart.timeScale?.();
  const visibleRange = timeScale?.getVisibleLogicalRange?.() || null;
  const rightCoordinateRaw = visibleRange
    ? timeScale?.logicalToCoordinate?.(visibleRange.to)
    : null;
  const rightCoordinate = Number.isFinite(rightCoordinateRaw)
    ? Math.max(0, Math.min(rightCoordinateRaw, width))
    : width;
  const barSpacing = estimateBarSpacing(chart, width);
  const markerOffset = barSpacing * 0.2;
  const guideOffset = barSpacing * 0.28;

  overlay.segments.forEach((segment) => {
    if (
      !segment ||
      !Number.isFinite(segment.value) ||
      (!Number.isFinite(segment.startIndex) &&
        !Number.isFinite(segment.startTime))
    ) {
      return;
    }

    const startCoordinateRaw = Number.isFinite(segment.startIndex)
      ? timeScale?.logicalToCoordinate?.(segment.startIndex)
      : timeScale?.timeToCoordinate?.(segment.startTime);
    const yCoordinate = series.priceToCoordinate?.(segment.value);

    if (
      !Number.isFinite(startCoordinateRaw) ||
      !Number.isFinite(yCoordinate) ||
      yCoordinate < -2 ||
      yCoordinate > height + 2
    ) {
      return;
    }

    const segmentStart = Math.min(startCoordinateRaw, rightCoordinate);
    const segmentEnd =
      segment.kind === "entry"
        ? Math.min(
            segmentStart +
              markerOffset +
              Math.max(rightCoordinate - segmentStart - markerOffset, 0) * 0.25,
            rightCoordinate
          )
        : rightCoordinate;
    const drawStart =
      segment.kind === "entry"
        ? Math.min(segmentStart + guideOffset, segmentEnd)
        : segmentStart;

    if (!Number.isFinite(segmentEnd) || segmentEnd <= drawStart) {
      return;
    }

    context.save();
    context.strokeStyle = segment.color;
    context.lineWidth = segment.lineWidth || 2;
    context.lineCap = segment.dashed ? "butt" : "round";
    if (segment.dashed) {
      context.setLineDash([6, 5]);
    } else {
      context.setLineDash([]);
    }
    const alignedY = Math.round(yCoordinate) + 0.5;
    context.beginPath();
    context.moveTo(drawStart, alignedY);
    context.lineTo(segmentEnd, alignedY);
    context.stroke();
    context.restore();
  });
};

const colorForOverlayRole = (role) => {
  const palette = getPalette();
  switch (String(role || "").toLowerCase()) {
    case "support":
      return palette.target;
    case "resistance":
      return palette.stop;
    case "entry":
      return palette.entry;
    case "stop_loss":
      return palette.stop;
    case "price_target":
      return palette.target;
    case "vwap":
      return palette.extendedHours;
    default:
      return isDarkMode ? "rgba(203,213,225,0.84)" : "rgba(71,85,105,0.84)";
  }
};

const colorWithAlpha = (color, alpha) => {
  const normalizedAlpha = Math.max(0, Math.min(Number(alpha) || 0, 1));
  const hexAlpha = Math.round(normalizedAlpha * 255).toString(16).padStart(2, "0");
  if (typeof color === "string" && /^#[0-9a-f]{6}$/i.test(color)) {
    return `${color}${hexAlpha}`;
  }
  const rgb = String(color || "").match(/^rgba?\(([^)]+)\)$/i);
  if (rgb) {
    const parts = rgb[1].split(",").slice(0, 3).map((part) => part.trim());
    return `rgba(${parts.join(", ")}, ${normalizedAlpha})`;
  }
  return `rgba(100, 116, 139, ${normalizedAlpha})`;
};

const overlayAppliesToTicker = (item, selectedTickerId) => {
  const itemTickerId = item?.ticker_id ?? item?.tickerId ?? null;
  if (itemTickerId == null || selectedTickerId == null) return true;
  return String(itemTickerId) === String(selectedTickerId);
};

const buildAgenticOverlayConfig = ({ agenticChartState, selectedTickerId }) => {
  if (!agenticChartState || typeof agenticChartState !== "object") {
    return { priceLines: [], zones: [] };
  }

  const overlays = Array.isArray(agenticChartState.overlays)
    ? agenticChartState.overlays
    : [];
  const priceLines = [];
  const zones = [];

  overlays.forEach((overlay) => {
    if (!overlay || typeof overlay !== "object") return;
    if (!overlayAppliesToTicker(overlay, selectedTickerId)) return;

    const kind = String(overlay.kind || "").toLowerCase();
    const semanticRole = overlay.semantic_role || overlay.semanticRole || "note";
    const color = colorForOverlayRole(semanticRole);
    const label =
      overlay.label ||
      String(semanticRole || "Level").replace(/_/g, " ");

    if (kind === "horizontal_line") {
      const value = toTradeNumber(overlay.price ?? overlay.value);
      if (value == null) return;
      priceLines.push({
        value,
        color,
        label,
        lineWidth: 2,
        dashed: semanticRole !== "entry",
      });
      return;
    }

    if (kind === "price_zone") {
      const top = toTradeNumber(overlay.top ?? overlay.upper ?? overlay.high);
      const bottom = toTradeNumber(overlay.bottom ?? overlay.lower ?? overlay.low);
      if (top == null || bottom == null) return;
      zones.push({
        top: Math.max(top, bottom),
        bottom: Math.min(top, bottom),
        color,
        label,
      });
    }
  });

  return { priceLines, zones };
};

const drawAgenticOverlay = ({ series, overlay, context, width, height }) => {
  if (!series || !context || !Number.isFinite(width) || !Number.isFinite(height)) {
    return;
  }

  const zones = Array.isArray(overlay?.zones) ? overlay.zones : [];
  zones.forEach((zone) => {
    const yTop = series.priceToCoordinate?.(zone.top);
    const yBottom = series.priceToCoordinate?.(zone.bottom);
    if (!Number.isFinite(yTop) || !Number.isFinite(yBottom)) return;

    const top = Math.max(0, Math.min(yTop, yBottom));
    const bottom = Math.min(height, Math.max(yTop, yBottom));
    if (bottom <= top) return;

    context.save();
    context.fillStyle = colorWithAlpha(zone.color || "#64748b", 0.13);
    context.strokeStyle = colorWithAlpha(zone.color || "#64748b", 0.5);
    context.lineWidth = 1;
    context.setLineDash([6, 5]);
    context.fillRect(0, top, width, bottom - top);
    context.strokeRect(0.5, top + 0.5, Math.max(width - 1, 0), Math.max(bottom - top - 1, 0));
    if (zone.label) {
      context.setLineDash([]);
      context.fillStyle = zone.color || getPalette().text;
      context.font = "600 11px system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
      context.textAlign = "right";
      context.textBaseline = "top";
      context.fillText(zone.label, width - 8, top + 5);
    }
    context.restore();
  });
};

const drawOverlayCanvas = ({
  chart,
  series,
  sessionOverlay,
  tradeOverlay,
  agenticOverlay,
  canvas,
}) => {
  if (!canvas) return;

  const resized = resizeOverlayCanvas(canvas);
  if (!resized) return;

  const { context, width, height } = resized;
  context.clearRect(0, 0, width, height);

  drawSessionOverlay({
    chart,
    overlay: sessionOverlay,
    context,
    width,
    height,
  });
  drawTradeOverlay({
    chart,
    series,
    overlay: tradeOverlay,
    context,
    width,
    height,
  });
  drawAgenticOverlay({
    series,
    overlay: agenticOverlay,
    context,
    width,
    height,
  });
};

const buildTradeOverlayConfig = ({
  messageThread,
  selectedTickerId,
  assetType,
  tickerPrices,
  scale,
  priceTimes,
}) => {
  const refinedQueryObj =
    messageThread?.refinedQueryObj &&
    typeof messageThread.refinedQueryObj === "object"
      ? messageThread.refinedQueryObj
      : null;
  const tradeProposal =
    refinedQueryObj?.trade_proposed || refinedQueryObj?.tradeProposed || null;

  if (!tradeProposal) {
    return { segments: [], markers: [], priceLines: [] };
  }

  const tradeEntry = toTradeNumber(
    tradeProposal.entry_price ?? tradeProposal.entryPrice
  );
  const tradeStop = toTradeNumber(
    tradeProposal.stop_loss ?? tradeProposal.stopLoss
  );
  const tradeTarget = toTradeNumber(
    tradeProposal.price_target ??
      tradeProposal.priceTarget ??
      tradeProposal.profit_target ??
      tradeProposal.profitTarget
  );
  const tradeSide = inferTradeSide(tradeEntry, tradeStop, tradeTarget);
  const tradeTickerId =
    tradeProposal?.ticker_id ?? tradeProposal?.tickerId ?? null;
  const tradeTickerIdStr =
    tradeTickerId != null ? String(tradeTickerId) : null;
  const selectedTickerIdStr =
    selectedTickerId != null ? String(selectedTickerId) : null;

  if (
    tradeEntry == null &&
    tradeStop == null &&
    tradeTarget == null
  ) {
    return { segments: [], markers: [], priceLines: [] };
  }

  if (
    tradeTickerIdStr &&
    selectedTickerIdStr &&
    tradeTickerIdStr !== selectedTickerIdStr
  ) {
    return { segments: [], markers: [], priceLines: [] };
  }

  const palette = getPalette();
  const entryColor = palette.entry;
  const rawAnchorIndex = buildTradeAnchorIndex({
    tradeProposal,
    messageThread,
    tickerPrices,
    scale,
  });
  const anchorIndex =
    rawAnchorIndex == null || !Array.isArray(priceTimes) || priceTimes.length === 0
      ? null
      : Math.max(0, Math.min(rawAnchorIndex, priceTimes.length - 1));
  const anchorTime =
    anchorIndex != null ? priceTimes[anchorIndex] : null;

  const segments = [];
  const priceLines = [];
  if (anchorIndex != null && tradeTarget != null) {
    segments.push({
      kind: "target",
      startIndex: anchorIndex,
      startTime: anchorTime,
      value: tradeTarget,
      color: palette.target,
      lineWidth: 2,
      dashed: true,
    });
    priceLines.push({
      label: "Target",
      value: tradeTarget,
      color: palette.target,
      lineWidth: 2,
      dashed: true,
    });
  }
  if (anchorIndex != null && tradeStop != null) {
    segments.push({
      kind: "stop",
      startIndex: anchorIndex,
      startTime: anchorTime,
      value: tradeStop,
      color: palette.stop,
      lineWidth: 2,
      dashed: true,
    });
    priceLines.push({
      label: "Stop",
      value: tradeStop,
      color: palette.stop,
      lineWidth: 2,
      dashed: true,
    });
  }
  if (anchorIndex != null && tradeEntry != null) {
    segments.push({
      kind: "entry",
      startIndex: anchorIndex,
      startTime: anchorTime,
      value: tradeEntry,
      color: entryColor,
      lineWidth: 2,
      dashed: false,
    });
    priceLines.push({
      label: "Entry",
      value: tradeEntry,
      color: entryColor,
      lineWidth: 2,
      dashed: false,
    });
  }

  const markers = [];
  if (anchorTime != null && tradeEntry != null) {
    markers.push({
      time: anchorTime,
      position: tradeSide === "short" ? "aboveBar" : "belowBar",
      color: entryColor,
      shape:
        tradeSide === "short"
          ? "arrowDown"
          : tradeSide === "long"
            ? "arrowUp"
            : "circle",
      text: "",
    });
  }

  return { segments, markers, priceLines };
};

const INDICATOR_COLORS = [
  "#f59e0b",
  "#06b6d4",
  "#a855f7",
  "#ef4444",
  "#22c55e",
  "#64748b",
];

const closeValue = (candle) => {
  const value = candle?.close ?? candle?.value;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const movingAverageData = (candleData, length) => {
  const windowLength = Math.max(1, Number(length) || 20);
  const result = [];
  let sum = 0;
  const values = [];

  candleData.forEach((candle) => {
    const close = closeValue(candle);
    values.push(close);
    if (close != null) sum += close;
    if (values.length > windowLength) {
      const removed = values.shift();
      if (removed != null) sum -= removed;
    }
    if (values.length === windowLength && values.every((value) => value != null)) {
      result.push({ time: candle.time, value: sum / windowLength });
    }
  });

  return result;
};

const exponentialAverageData = (candleData, length) => {
  const windowLength = Math.max(1, Number(length) || 20);
  const multiplier = 2 / (windowLength + 1);
  const result = [];
  let ema = null;

  candleData.forEach((candle) => {
    const close = closeValue(candle);
    if (close == null) return;
    ema = ema == null ? close : close * multiplier + ema * (1 - multiplier);
    result.push({ time: candle.time, value: ema });
  });

  return result;
};

const vwapData = (candleData) => {
  let cumulativePriceVolume = 0;
  let cumulativeVolume = 0;
  const result = [];

  candleData.forEach((candle) => {
    const high = Number.isFinite(candle?.high) ? candle.high : closeValue(candle);
    const low = Number.isFinite(candle?.low) ? candle.low : closeValue(candle);
    const close = closeValue(candle);
    const volume = Number(candle?.volume);
    if (high == null || low == null || close == null || !Number.isFinite(volume) || volume <= 0) return;

    cumulativePriceVolume += ((high + low + close) / 3) * volume;
    cumulativeVolume += volume;
    if (cumulativeVolume > 0) {
      result.push({ time: candle.time, value: cumulativePriceVolume / cumulativeVolume });
    }
  });

  return result;
};

const bollingerData = (candleData, length, multiplier) => {
  const windowLength = Math.max(1, Number(length) || 20);
  const mult = Number.isFinite(Number(multiplier)) ? Number(multiplier) : 2;
  const upper = [];
  const middle = [];
  const lower = [];
  const values = [];

  candleData.forEach((candle) => {
    const close = closeValue(candle);
    values.push(close);
    if (values.length > windowLength) values.shift();
    if (values.length !== windowLength || values.some((value) => value == null)) return;

    const mean = values.reduce((sum, value) => sum + value, 0) / windowLength;
    const variance =
      values.reduce((sum, value) => sum + (value - mean) ** 2, 0) / windowLength;
    const deviation = Math.sqrt(variance);
    middle.push({ time: candle.time, value: mean });
    upper.push({ time: candle.time, value: mean + deviation * mult });
    lower.push({ time: candle.time, value: mean - deviation * mult });
  });

  return { upper, middle, lower };
};

const buildIndicatorSeriesConfigs = ({ agenticChartState, selectedTickerId, candleData }) => {
  if (!agenticChartState || typeof agenticChartState !== "object") return [];
  if (!Array.isArray(candleData) || candleData.length === 0) return [];

  const indicators = Array.isArray(agenticChartState.indicators)
    ? agenticChartState.indicators
    : [];
  const configs = [];

  indicators.forEach((indicator, index) => {
    if (!indicator || typeof indicator !== "object") return;
    if (!overlayAppliesToTicker(indicator, selectedTickerId)) return;
    if (String(indicator.pane || "main").toLowerCase() !== "main") return;

    const kind = String(indicator.kind || "").toLowerCase();
    const params = indicator.params || {};
    const color = INDICATOR_COLORS[index % INDICATOR_COLORS.length];
    const label = indicator.label || kind.toUpperCase();

    if (kind === "sma") {
      configs.push({
        id: indicator.id || `${kind}-${params.length || 20}`,
        label,
        color,
        data: movingAverageData(candleData, params.length || 20),
      });
    } else if (kind === "ema") {
      configs.push({
        id: indicator.id || `${kind}-${params.length || 20}`,
        label,
        color,
        data: exponentialAverageData(candleData, params.length || 20),
      });
    } else if (kind === "vwap") {
      configs.push({
        id: indicator.id || "vwap",
        label,
        color,
        data: vwapData(candleData),
      });
    } else if (kind === "bollinger_bands") {
      const bands = bollingerData(
        candleData,
        params.length || 20,
        params.multiplier || 2,
      );
      configs.push({
        id: `${indicator.id || "bollinger"}-upper`,
        label: `${label} upper`,
        color,
        data: bands.upper,
      });
      configs.push({
        id: `${indicator.id || "bollinger"}-middle`,
        label: `${label} mid`,
        color: colorWithAlpha(color, 0.72),
        data: bands.middle,
        lineWidth: 1,
      });
      configs.push({
        id: `${indicator.id || "bollinger"}-lower`,
        label: `${label} lower`,
        color,
        data: bands.lower,
      });
    }
  });

  return configs.filter((config) => config.data.length > 0);
};

const ChartCanvas = memo(
  ({
    idx,
    messageThread,
    chartConfigKey,
    hasRendered,
    setHasRendered,
    selectedScale,
    chartViewType,
    tickerPrices,
    assetType: assetTypeOverride,
    extendedHoursQuote = null,
    isChartVisible = true,
    selectedTickerId = null,
    tradeOverlayKey = "",
    agenticChartState = null,
    agenticOverlayKey = "",
    sharedRangeBus = null,
    onChartReady,
    canvasId,
    canvasStyle,
    userTimezone = null,
  }) => {
    const isMobile = isMobileBrowser();
    const scale = normalizeChartScale(selectedScale);
    const assetType = assetTypeOverride || messageThread?.assetType || null;
    const containerRef = useRef(null);
    const chartRef = useRef(null);
    const chartHandleRef = useRef(null);
    const seriesRef = useRef(null);
    const seriesKindRef = useRef(null);
    const priceLineRefsRef = useRef([]);
    const indicatorSeriesRefsRef = useRef([]);
    const resizeObserverRef = useRef(null);
    const attributionObserverRef = useRef(null);
    const pointCountRef = useRef(0);
    const suppressRangeSyncRef = useRef(false);
    const syncIdRef = useRef(`chart-sync-${idx}-${canvasId || "main"}`);
    const ohlcRef = useRef(null);
    const livePriceLabelRef = useRef(null);
    const tradeOverlayCanvasRef = useRef(null);
    const tradeOverlayConfigRef = useRef({
      segments: [],
      markers: [],
      priceLines: [],
    });
    const agenticOverlayConfigRef = useRef({
      priceLines: [],
      zones: [],
    });
    const tradeOverlayDrawRef = useRef(() => {});
    const tradeOverlayFrameRef = useRef(null);
    const sessionOverlayConfigRef = useRef(SESSION_OVERLAY_EMPTY);
    const candleLookupRef = useRef(new Map());
    const latestCandleRef = useRef(null);
    const [library, setLibrary] = useState(
      () => window.LightweightCharts || null
    );
    const [loadError, setLoadError] = useState(null);

    const priceSeries = useMemo(
      () => createMainSeriesData(tickerPrices || [], userTimezone),
      [tickerPrices, userTimezone]
    );

    pointCountRef.current = priceSeries.times.length;

    const scheduleTradeOverlayDraw = () => {
      if (typeof window === "undefined") return;
      if (tradeOverlayFrameRef.current != null) {
        window.cancelAnimationFrame(tradeOverlayFrameRef.current);
      }
      tradeOverlayFrameRef.current = window.requestAnimationFrame(() => {
        tradeOverlayFrameRef.current = null;
        tradeOverlayDrawRef.current?.();
      });
    };

    const syncLivePriceLabel = () => {
      updateLivePriceLabel({
        element: livePriceLabelRef.current,
        chart: chartRef.current,
        series: seriesRef.current,
        latestCandle: latestCandleRef.current,
        scale,
        assetType,
        isMobile,
        extendedHoursQuote,
      });
    };

    const clearIndicatorSeries = (chart = chartRef.current) => {
      if (!chart) {
        indicatorSeriesRefsRef.current = [];
        return;
      }
      indicatorSeriesRefsRef.current.forEach((indicatorSeries) => {
        try {
          chart.removeSeries?.(indicatorSeries);
        } catch (_error) {}
      });
      indicatorSeriesRefsRef.current = [];
    };

    useEffect(() => {
      let cancelled = false;

      loadLightweightCharts()
        .then((api) => {
          if (cancelled) return;
          setLibrary(api);
        })
        .catch((error) => {
          if (cancelled) return;
          setLoadError(error);
        });

      return () => {
        cancelled = true;
      };
    }, []);

    useEffect(() => {
      if (!library || !isChartVisible || !containerRef.current) return undefined;

      const container = containerRef.current;
      const chart = library.createChart(container, {
        width: Math.max(container.clientWidth || 0, 320),
        height: Math.max(container.clientHeight || 0, 220),
        ...buildChartOptions({ library, scale, assetType, isMobile }),
      });

      chart.applyOptions({ attributionLogo: { visible: false } });
      chartRef.current = chart;

      attributionObserverRef.current = installAttributionHider(container);
      resetOhlcBox(ohlcRef.current);
      hideLivePriceLabel(livePriceLabelRef.current);

      const pushSharedRange = (range) => {
        if (!sharedRangeBus?.setRange) return;
        if (!range || !Number.isFinite(range.from) || !Number.isFinite(range.to)) {
          return;
        }
        if (suppressRangeSyncRef.current) return;
        sharedRangeBus.setRange(range, syncIdRef.current);
      };

      const timeScale = chart.timeScale();
      const handleVisibleRangeChange = (range) => {
        pushSharedRange(range);
        scheduleTradeOverlayDraw();
        syncLivePriceLabel();
      };

      if (timeScale?.subscribeVisibleLogicalRangeChange) {
        timeScale.subscribeVisibleLogicalRangeChange(handleVisibleRangeChange);
      }

      const handleCrosshairMove = (param) => {
        if (!param?.time) {
          updateOhlcBox(ohlcRef.current, latestCandleRef.current, assetType);
          return;
        }

        let candle = candleLookupRef.current.get(param.time) || null;
        if (
          !candle &&
          typeof param.seriesData?.get === "function" &&
          seriesRef.current
        ) {
          const hovered = param.seriesData.get(seriesRef.current);
          if (
            hovered &&
            Number.isFinite(hovered.open) &&
            Number.isFinite(hovered.high) &&
            Number.isFinite(hovered.low) &&
            Number.isFinite(hovered.close)
          ) {
            candle = hovered;
          }
        }

        if (!candle) {
          candle = latestCandleRef.current;
        }
        updateOhlcBox(ohlcRef.current, candle, assetType);
      };

      if (chart.subscribeCrosshairMove) {
        chart.subscribeCrosshairMove(handleCrosshairMove);
      }

      const unsubscribeSharedRange = sharedRangeBus?.subscribe?.(
        (range, sourceId) => {
          if (sourceId === syncIdRef.current) return;
          if (!range || !Number.isFinite(range.from) || !Number.isFinite(range.to)) {
            return;
          }
          const current = chart.timeScale()?.getVisibleLogicalRange?.();
          if (logicalRangesClose(current, range)) return;
          scheduleRangeApply({
            chart,
            range,
            suppressRangeSyncRef,
          });
        }
      );

      if (typeof ResizeObserver !== "undefined") {
        resizeObserverRef.current = new ResizeObserver((entries) => {
          const entry = entries[0];
          if (!entry) return;
          const width = Math.max(entry.contentRect.width || 0, 320);
          const height = Math.max(entry.contentRect.height || 0, 220);
          chart.resize(width, height);
          scheduleTradeOverlayDraw();
          syncLivePriceLabel();
        });
        resizeObserverRef.current.observe(container);
      }

      chartHandleRef.current = {
        zoom: (factor) => {
          const activeChart = chartRef.current;
          if (!activeChart) return;
          const range =
            activeChart.timeScale()?.getVisibleLogicalRange?.() ||
            getInitialLogicalRange(pointCountRef.current, scale);
          const nextRange = zoomLogicalRange(range, factor);
          if (!nextRange) return;
          scheduleRangeApply({
            chart: activeChart,
            range: nextRange,
            suppressRangeSyncRef,
          });
        },
        getVisibleLogicalRange: () =>
          chartRef.current?.timeScale()?.getVisibleLogicalRange?.() || null,
        setVisibleLogicalRange: (range) => {
          if (!chartRef.current || !range) return;
          scheduleRangeApply({
            chart: chartRef.current,
            range,
            suppressRangeSyncRef,
          });
        },
        resetPriceScale: () => {
          const activeChart = chartRef.current;
          const activeSeries = seriesRef.current;
          const priceScale =
            (typeof activeSeries?.priceScale === "function"
              ? activeSeries.priceScale()
              : null) ||
            (typeof activeChart?.priceScale === "function"
              ? activeChart.priceScale("right")
              : null);

          if (typeof priceScale?.applyOptions !== "function") return;

          priceScale.applyOptions({ autoScale: false });
          window.requestAnimationFrame(() => {
            priceScale.applyOptions({ autoScale: true });
            scheduleTradeOverlayDraw();
          });
        },
        getScreenshotCanvas: () => {
          const baseCanvas = resolveChartScreenshotCanvas(
            chartRef.current,
            containerRef.current
          );
          const overlayCanvas = tradeOverlayCanvasRef.current;
          if (!baseCanvas || !overlayCanvas) {
            return baseCanvas;
          }

          const composite = document.createElement("canvas");
          composite.width = baseCanvas.width;
          composite.height = baseCanvas.height;
          const context = composite.getContext("2d");
          if (!context) {
            return baseCanvas;
          }

          context.drawImage(baseCanvas, 0, 0);
          context.drawImage(
            overlayCanvas,
            0,
            0,
            overlayCanvas.width,
            overlayCanvas.height,
            0,
            0,
            composite.width,
            composite.height
          );
          return composite;
        },
      };

      if (typeof onChartReady === "function") {
        onChartReady(chartHandleRef.current);
      }

      return () => {
        if (timeScale?.unsubscribeVisibleLogicalRangeChange) {
          timeScale.unsubscribeVisibleLogicalRangeChange(handleVisibleRangeChange);
        }
        if (chart.unsubscribeCrosshairMove) {
          chart.unsubscribeCrosshairMove(handleCrosshairMove);
        }
        if (typeof unsubscribeSharedRange === "function") {
          unsubscribeSharedRange();
        }
        if (
          tradeOverlayFrameRef.current != null &&
          typeof window !== "undefined"
        ) {
          window.cancelAnimationFrame(tradeOverlayFrameRef.current);
          tradeOverlayFrameRef.current = null;
        }
        if (resizeObserverRef.current) {
          resizeObserverRef.current.disconnect();
          resizeObserverRef.current = null;
        }
        if (attributionObserverRef.current) {
          attributionObserverRef.current.disconnect();
          attributionObserverRef.current = null;
        }
        if (typeof onChartReady === "function") {
          onChartReady(null);
        }
        clearIndicatorSeries(chart);
        priceLineRefsRef.current = [];
        seriesKindRef.current = null;
        seriesRef.current = null;
        latestCandleRef.current = null;
        tradeOverlayDrawRef.current = () => {};
        tradeOverlayConfigRef.current = {
          segments: [],
          markers: [],
          priceLines: [],
        };
        agenticOverlayConfigRef.current = {
          priceLines: [],
          zones: [],
        };
        sessionOverlayConfigRef.current = SESSION_OVERLAY_EMPTY;
        clearOverlayCanvas(tradeOverlayCanvasRef.current);
        hideLivePriceLabel(livePriceLabelRef.current);
        chartHandleRef.current = null;
        chartRef.current?.remove();
        chartRef.current = null;
      };
    }, [library, isChartVisible, isMobile, onChartReady, scale, assetType, sharedRangeBus, idx, canvasId]);

    useEffect(() => {
      if (!chartRef.current) return;
      chartRef.current.applyOptions(
        buildChartOptions({ library, scale, assetType, isMobile })
      );
      chartRef.current.applyOptions({ attributionLogo: { visible: false } });
    }, [library, scale, assetType, isMobile]);

    useEffect(() => {
      if (!chartRef.current || !library || priceSeries.candleData.length === 0) {
        latestCandleRef.current = null;
        if (
          seriesRef.current &&
          typeof seriesRef.current.setMarkers === "function"
        ) {
          seriesRef.current.setMarkers([]);
        }
        if (seriesRef.current) {
          priceLineRefsRef.current.forEach((priceLine) => {
            try {
              seriesRef.current.removePriceLine?.(priceLine);
            } catch (_error) {}
          });
        }
        clearIndicatorSeries();
        priceLineRefsRef.current = [];
        tradeOverlayConfigRef.current = {
          segments: [],
          markers: [],
          priceLines: [],
        };
        agenticOverlayConfigRef.current = {
          priceLines: [],
          zones: [],
        };
        sessionOverlayConfigRef.current = SESSION_OVERLAY_EMPTY;
        tradeOverlayDrawRef.current = () => {
          clearOverlayCanvas(tradeOverlayCanvasRef.current);
        };
        resetOhlcBox(ohlcRef.current);
        hideLivePriceLabel(livePriceLabelRef.current);
        scheduleTradeOverlayDraw();
        return;
      }

      const chart = chartRef.current;
      const palette = getPalette();
      clearIndicatorSeries(chart);
      const desiredSeriesKind =
        chartViewType === "line" ? "line" : "candlestick";
      candleLookupRef.current = new Map(
        priceSeries.candleData.map((entry) => [entry.time, entry])
      );
      latestCandleRef.current =
        priceSeries.candleData[priceSeries.candleData.length - 1] || null;

      let nextSeries = seriesRef.current;
      const canReuseSeries =
        nextSeries && seriesKindRef.current === desiredSeriesKind;

      if (!canReuseSeries && seriesRef.current) {
        chart.removeSeries(seriesRef.current);
        seriesRef.current = null;
        seriesKindRef.current = null;
        priceLineRefsRef.current = [];
        nextSeries = null;
      }

      if (desiredSeriesKind === "line") {
        if (!nextSeries) {
          nextSeries = chart.addLineSeries({
            color: palette.line,
            lineWidth: 2,
            crosshairMarkerVisible: false,
            lastValueVisible: false,
            priceLineVisible: true,
          });
          seriesKindRef.current = desiredSeriesKind;
        } else {
          nextSeries.applyOptions?.({
            color: palette.line,
            lineWidth: 2,
            crosshairMarkerVisible: false,
            lastValueVisible: false,
            priceLineVisible: true,
          });
        }
        nextSeries.setData(priceSeries.lineData);
      } else {
        if (!nextSeries) {
          nextSeries = chart.addCandlestickSeries({
            upColor: palette.up,
            downColor: palette.down,
            borderUpColor: palette.up,
            borderDownColor: palette.down,
            wickUpColor: palette.up,
            wickDownColor: palette.down,
            lastValueVisible: false,
            priceLineVisible: true,
          });
          seriesKindRef.current = desiredSeriesKind;
        } else {
          nextSeries.applyOptions?.({
            upColor: palette.up,
            downColor: palette.down,
            borderUpColor: palette.up,
            borderDownColor: palette.down,
            wickUpColor: palette.up,
            wickDownColor: palette.down,
            lastValueVisible: false,
            priceLineVisible: true,
          });
        }
        nextSeries.setData(priceSeries.candleData);
      }

      const tradeOverlay = buildTradeOverlayConfig({
        messageThread,
        selectedTickerId,
        assetType,
        tickerPrices,
        scale,
        priceTimes: priceSeries.times,
      });
      const agenticOverlay = buildAgenticOverlayConfig({
        agenticChartState,
        selectedTickerId,
      });
      const sessionOverlay = buildSessionOverlayConfig({
        candleData: priceSeries.candleData,
        assetType,
        scale,
      });
      const indicatorSeriesConfigs = buildIndicatorSeriesConfigs({
        agenticChartState,
        selectedTickerId,
        candleData: priceSeries.candleData,
      });
      const extendedPrice = toTradeNumber(extendedHoursQuote?.price);
      const extendedHoursPriceLine =
        extendedPrice != null
          ? {
              value: extendedPrice,
              color: palette.extendedHours,
              lineWidth: 1,
              dashed: true,
              label: "",
              axisLabelVisible: false,
            }
          : null;

      tradeOverlayConfigRef.current = tradeOverlay;
      agenticOverlayConfigRef.current = agenticOverlay;
      sessionOverlayConfigRef.current = sessionOverlay;

      if (nextSeries.setMarkers) {
        nextSeries.setMarkers(tradeOverlay.markers);
      }
      indicatorSeriesConfigs.forEach((indicatorConfig) => {
        const indicatorSeries = chart.addLineSeries?.({
          color: indicatorConfig.color,
          lineWidth: indicatorConfig.lineWidth || 2,
          crosshairMarkerVisible: false,
          lastValueVisible: false,
          priceLineVisible: false,
        });
        if (!indicatorSeries) return;
        indicatorSeries.setData(indicatorConfig.data);
        indicatorSeriesRefsRef.current.push(indicatorSeries);
      });
      priceLineRefsRef.current.forEach((priceLine) => {
        try {
          nextSeries.removePriceLine?.(priceLine);
        } catch (_error) {}
      });
      priceLineRefsRef.current = [];
      const priceLineConfigs = extendedHoursPriceLine
        ? [...tradeOverlay.priceLines, ...agenticOverlay.priceLines, extendedHoursPriceLine]
        : [...tradeOverlay.priceLines, ...agenticOverlay.priceLines];
      priceLineConfigs.forEach((priceLineConfig) => {
        if (
          !Number.isFinite(priceLineConfig?.value) ||
          typeof nextSeries.createPriceLine !== "function"
        ) {
          return;
        }
        const priceLine = nextSeries.createPriceLine({
          price: priceLineConfig.value,
          color: priceLineConfig.color,
          lineWidth: priceLineConfig.lineWidth || 2,
          lineStyle: priceLineConfig.dashed
            ? library?.LineStyle?.Dashed ?? 2
            : library?.LineStyle?.Solid ?? 0,
          axisLabelVisible: priceLineConfig.axisLabelVisible !== false,
          title: priceLineConfig.label || "",
          lineVisible: true,
        });
        if (priceLine) {
          priceLineRefsRef.current.push(priceLine);
        }
      });

      seriesRef.current = nextSeries;
      tradeOverlayDrawRef.current = () => {
        drawOverlayCanvas({
          chart,
          series: nextSeries,
          sessionOverlay: sessionOverlayConfigRef.current,
          tradeOverlay: tradeOverlayConfigRef.current,
          agenticOverlay: agenticOverlayConfigRef.current,
          canvas: tradeOverlayCanvasRef.current,
        });
      };

      const nextRange =
        sharedRangeBus?.getRange?.() ||
        getInitialLogicalRange(priceSeries.times.length, scale);
      const currentRange =
        chart.timeScale()?.getVisibleLogicalRange?.() || null;

      if (nextRange && !logicalRangesClose(currentRange, nextRange)) {
        scheduleRangeApply({
          chart,
          range: nextRange,
          suppressRangeSyncRef,
        });
        if (!sharedRangeBus?.getRange?.() && sharedRangeBus?.setRange) {
          sharedRangeBus.setRange(nextRange, syncIdRef.current);
        }
      }

      if (!hasRendered) {
        setHasRendered(true);
      }

      updateOhlcBox(ohlcRef.current, latestCandleRef.current, assetType);
      syncLivePriceLabel();
      scheduleTradeOverlayDraw();
    }, [
      library,
      messageThread,
      selectedTickerId,
      chartViewType,
      priceSeries,
      scale,
      assetType,
      tradeOverlayKey,
      agenticOverlayKey,
      agenticChartState,
      chartConfigKey,
      extendedHoursQuote,
      hasRendered,
      setHasRendered,
      sharedRangeBus,
    ]);

    useEffect(() => {
      if (typeof window === "undefined") return undefined;

      syncLivePriceLabel();
      const intervalId = window.setInterval(() => {
        syncLivePriceLabel();
      }, 1000);

      return () => {
        window.clearInterval(intervalId);
      };
    }, [scale, chartViewType, selectedTickerId, isChartVisible, assetType, extendedHoursQuote]);

    const resolvedStyle = useMemo(
      () => ({
        width: "100%",
        height: "100%",
        display: "block",
        position: "relative",
        ...(canvasStyle || {}),
        ...(isMobile
          ? {
              touchAction: "pan-y pinch-zoom",
              WebkitUserSelect: "none",
              WebkitTouchCallout: "none",
            }
          : {}),
      }),
      [canvasStyle, isMobile]
    );

    if (loadError) {
      return (
        <div
          id={canvasId}
          style={{
            ...resolvedStyle,
            color: getPalette().mutedText,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "13px",
          }}
        >
          Chart unavailable
        </div>
      );
    }

    return (
      <div id={canvasId || `chart-${idx}`} style={resolvedStyle}>
        <div
          ref={containerRef}
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 1,
          }}
        />
        <canvas
          ref={tradeOverlayCanvasRef}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            pointerEvents: "none",
            zIndex: 3,
          }}
        />
        <div
          ref={livePriceLabelRef}
          style={{
            position: "absolute",
            right: 0,
            zIndex: 4,
            display: "none",
            pointerEvents: "none",
            transform: "translateY(-50%)",
            padding: isMobile ? "4px 7px" : "5px 8px",
            borderTopLeftRadius: "6px",
            borderBottomLeftRadius: "6px",
            border: "none",
            background: getPalette().up,
            color: "#ffffff",
            fontWeight: 700,
            lineHeight: 1,
            letterSpacing: "0.03em",
            fontVariantNumeric: "tabular-nums",
            whiteSpace: "nowrap",
            boxShadow: "0 4px 10px rgba(0,0,0,0.18)",
            display: "none",
            alignItems: "flex-start",
          }}
        />
        <div
          ref={ohlcRef}
          style={{
            position: "absolute",
            top: isMobile ? "24px" : 0,
            left: isMobile ? "2px" : "50%",
            transform: isMobile ? "none" : "translateX(-50%)",
            zIndex: 4,
            display: "flex",
            gap: isMobile ? "6px" : "10px",
            alignItems: "center",
            pointerEvents: "none",
            padding: isMobile ? "3px 7px" : "5px 10px",
            borderRadius: "999px",
            border: isDarkMode
              ? "1px solid rgba(255,255,255,0.08)"
              : "1px solid rgba(15,23,42,0.08)",
            background: isDarkMode
              ? "rgba(11, 13, 16, 0.72)"
              : "rgba(255, 255, 255, 0.78)",
            backdropFilter: "blur(8px)",
            boxShadow: isDarkMode
              ? "0 8px 18px rgba(0,0,0,0.35)"
              : "0 8px 18px rgba(15,23,42,0.12)",
            color: getPalette().text,
            fontSize: isMobile ? "10px" : "12px",
            fontWeight: 700,
            letterSpacing: "0.01em",
            whiteSpace: "nowrap",
          }}
        />
      </div>
    );
  },
  (prev, next) => {
    if (prev.idx !== next.idx) return false;
    if (prev.tickerPrices !== next.tickerPrices) return false;
    if (prev.selectedScale !== next.selectedScale) return false;
    if (prev.chartViewType !== next.chartViewType) return false;
    if (prev.isChartVisible !== next.isChartVisible) return false;
    if (prev.sharedRangeBus !== next.sharedRangeBus) return false;
    if (prev.selectedTickerId !== next.selectedTickerId) return false;
    if (prev.tradeOverlayKey !== next.tradeOverlayKey) return false;
    if (prev.agenticOverlayKey !== next.agenticOverlayKey) return false;
    if (prev.agenticChartState !== next.agenticChartState) return false;
    if (prev.canvasStyle !== next.canvasStyle) return false;
    if (prev.assetType !== next.assetType) return false;
    if (prev.extendedHoursQuote !== next.extendedHoursQuote) return false;
    if (prev.chartConfigKey !== next.chartConfigKey) return false;
    if (prev.onChartReady !== next.onChartReady) return false;
    if (prev.userTimezone !== next.userTimezone) return false;
    return true;
  }
);

ChartCanvas.displayName = "ChartCanvas";

export default ChartCanvas;
